import type { UseQueryOptions } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { RefreshClient } from "@citrine/client";
import { STATIC_QUERY_OPTIONS } from "@citrine/client/utils";
import { isUnitTest } from "@citrine/configuration";

import type { UnitConversion } from "./units";
import { createConversion, getFormattedUnits } from "./units";

export type FormattedUnitsLookup = Partial<Record<string, string>>;

const ENDPOINT = "/api/units";

export class UnitsClient extends RefreshClient {
  private static instance: UnitsClient;
  private cache: Partial<Record<string, Promise<UnitConversion>>> = {};

  protected constructor() {
    super();
    this.addToCache(
      createConversion("", {
        standard_units: "dimensionless",
        user_units: "dimensionless",
      }),
    );
  }

  static getInstance() {
    return (this.instance ??= new UnitsClient());
  }

  async formatUnits(unit: string) {
    return getFormattedUnits(await this.getUnitConversion(unit));
  }

  useFormatUnits(unit: string) {
    return this.useGetUnitConversion(unit, {
      select: getFormattedUnits,
    });
  }

  /**
   * Useful when you need to get a mapping of formatted units prior to executing
   * code that requires units as a string, i.e. calculating text width or generating
   * Plotly labels.
   *
   * For most situations where units formatting can be done with ReactNodes, use
   * the <FormattedUnits> component.
   */
  useFormatUnitsMap<Unit extends string>(units: Unit[]) {
    return this.useGetUnitConversionMap(units, {
      select: (unitConversionMap) =>
        Object.fromEntries(
          Object.entries(unitConversionMap)
            .filter((args): args is [Unit, UnitConversion] => Boolean(args[1]))
            .map(([key, value]) => [key, getFormattedUnits(value)]),
        ) as Record<Unit, string>,
    });
  }

  getUnitConversion(unit: string) {
    return (this.cache[unit] ??= isUnitTest
      ? Promise.resolve(createConversion(unit))
      : this.request<UnitConversion>({
          method: "GET",
          url: ENDPOINT,
          params: {
            units: unit,
          },
        })
          .catch((error) => {
            // if this is NOT an invalid unit (e.g., network error), remove the pending promise from the cache
            if (!(isAxiosError(error) && error.response?.status === 400)) {
              delete this.cache[unit];
            }
            throw error;
          })
          .then((unitConversion) => this.addToCache(unitConversion)));
  }

  useGetUnitConversion<Selection = UnitConversion>(
    unit: string,
    options?: Omit<
      UseQueryOptions<UnitConversion, Error, Selection>,
      "queryFn" | "queryKey"
    >,
  ) {
    return useQuery({
      ...STATIC_QUERY_OPTIONS,
      ...options,
      queryKey: ["units", unit],
      queryFn: () => this.getUnitConversion(unit),
    });
  }

  async getUnitConversionMap<Unit extends string>(units: Unit[]) {
    const unitsFromCache: Promise<[Unit, UnitConversion]>[] = [];
    const cacheMisses = [];
    for (const unit of units) {
      const cachedData = this.cache[unit];
      if (cachedData) {
        unitsFromCache.push(cachedData.then((data) => [unit, data]));
      } else {
        cacheMisses.push(unit);
      }
    }

    let additions: Partial<Record<Unit, UnitConversion>> = {};
    if (cacheMisses.length) {
      const request = this.fetchMany(cacheMisses);

      for (const missedUnit of cacheMisses) {
        this.cache[missedUnit] = request
          .catch((error) => {
            // we do not know which unit was invalid, so remove all from cache
            delete this.cache[missedUnit];
            throw error;
          })
          .then((data) => this.addToCache(data[missedUnit]!));
      }

      additions = await request;
    }

    return {
      ...Object.fromEntries(await Promise.all(unitsFromCache)),
      ...additions,
    } as Record<Unit, UnitConversion>;
  }

  useGetUnitConversionMap<
    Unit extends string,
    Selection = Record<Unit, UnitConversion>,
  >(
    units: Unit[],
    options?: Omit<
      UseQueryOptions<Record<Unit, UnitConversion>, Error, Selection>,
      "queryFn" | "queryKey"
    >,
  ) {
    return useQuery({
      ...options,
      queryKey: ["units", units.toSorted()],
      queryFn: () => this.getUnitConversionMap(units),
    });
  }

  // isolate network fetching of many units so we can mock for unit tests
  protected fetchMany<Unit extends string>(units: Unit[]) {
    return isUnitTest
      ? Promise.resolve(
          Object.fromEntries(
            units.map((unit) => [unit, createConversion(unit)]),
          ) as Partial<Record<Unit, UnitConversion>>,
        )
      : this.request<Partial<Record<Unit, UnitConversion>>>({
          method: "POST",
          url: ENDPOINT,
          data: units,
        });
  }

  // isolate network fetching of a single unit so we can mock for unit tests
  protected fetchOne(unit: string) {
    return isUnitTest
      ? Promise.resolve(createConversion(unit))
      : this.request<UnitConversion>({
          method: "GET",
          url: ENDPOINT,
          params: {
            units: unit,
          },
        });
  }

  private addToCache(unitConversion: UnitConversion) {
    // request was made with full or abbreviated units, add the other to the cache
    this.cache[unitConversion.user_units] = Promise.resolve(unitConversion);
    this.cache[unitConversion.short_user_units] =
      Promise.resolve(unitConversion);
    return unitConversion;
  }
}
